import { useEffect } from 'react'

/**
 * The colour a whole page sits on.
 *
 * ── Why a page gets to choose at all ───────────────────────────────────────
 *
 * There are six or seven screens in this app, not sixty. At that size the
 * background is a usable signal — you can know which screen you are on before
 * you have read a word of it — where in a large app it would just be noise
 * nobody could hold in their head. So each page names its own surface, and the
 * cream default is a choice rather than the only option.
 *
 * ── How it works ───────────────────────────────────────────────────────────
 *
 * Each surface is a block of token overrides on `[data-surface]` in worx.css.
 * Nothing restyles a component: `--bg`, `--surface`, `--ink`, `--muted` and
 * `--line` change, and the whole tree follows because every rule in this
 * codebase already reads those and not literal colours.
 *
 * The attribute goes on <html>, not on a wrapper div, because the colour has to
 * cover the overscroll area and the space below short content — a coloured div
 * inside a cream page is a rectangle, not a surface.
 *
 * ── Strong surfaces keep their cards light ─────────────────────────────────
 *
 * On teal, coral or black the page ink turns white. A `.card` then puts the
 * normal palette BACK, because a card is a sheet laid on the page and not part
 * of it. That rule lives with the surfaces in worx.css.
 *
 * ── Dark mode ──────────────────────────────────────────────────────────────
 *
 * A page that names a surface looks the same in both schemes: these are brand
 * decisions, and a splash that is white by decision is not white-unless-your-
 * phone-disagrees. Every page that names nothing keeps the system's answer.
 */
export type Surface = 'cream' | 'white' | 'gray' | 'teal' | 'coral' | 'ink'

/**
 * Paint the page while this component is mounted, and hand it back on the way
 * out. The cleanup is what lets a screen be opened and closed repeatedly
 * without the app ending up on whatever colour was painted last.
 */
export function usePageSurface(surface: Surface): void {
  useEffect(() => {
    const root = document.documentElement
    const previous = root.dataset.surface
    root.dataset.surface = surface
    return () => {
      if (previous === undefined) delete root.dataset.surface
      else root.dataset.surface = previous
    }
  }, [surface])
}
